import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type {
  Toast,
  ToastType,
  ToastOptions,
  ToastPosition,
  ToastStoreConfig,
} from '../types/toast.types'

const DEFAULT_CONFIG: ToastStoreConfig = {
  maxToasts: 5,
  defaultDuration: 5000,
  defaultPosition: 'top-right',
  pauseOnHover: true,
}

const DEFAULT_ICONS: Record<ToastType, string> = {
  success: 'mdi-check-circle',
  error: 'mdi-alert-circle',
  warning: 'mdi-alert',
  info: 'mdi-information',
}

export const useToastStore = defineStore('core-toast', () => {
  const toasts = ref<Toast[]>([])
  const config = ref<ToastStoreConfig>({ ...DEFAULT_CONFIG })

  const visibleToasts = computed(() => {
    const grouped: Record<ToastPosition, Toast[]> = {
      'top-right': [],
      'top-left': [],
      'top-center': [],
      'bottom-right': [],
      'bottom-left': [],
      'bottom-center': [],
    }

    for (const toast of toasts.value) {
      grouped[toast.position].push(toast)
    }

    for (const position of Object.keys(grouped) as ToastPosition[]) {
      grouped[position] = grouped[position].slice(0, config.value.maxToasts)
    }

    return grouped
  })

  const hasToasts = computed(() => toasts.value.length > 0)

  function generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  function add(message: string, options: ToastOptions = {}): string {
    const id = generateId()
    const type = options.type ?? 'info'
    const duration = options.duration ?? config.value.defaultDuration

    const toast: Toast = {
      id,
      message,
      type,
      duration,
      closable: options.closable ?? true,
      actions: options.actions ?? [],
      position: options.position ?? config.value.defaultPosition,
      icon: options.icon ?? DEFAULT_ICONS[type],
      createdAt: Date.now(),
      isPaused: false,
      remainingTime: typeof duration === 'number' ? duration : 0,
    }

    toasts.value.push(toast)
    return id
  }

  function remove(id: string): void {
    const index = toasts.value.findIndex((t) => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  function pause(id: string): void {
    const toast = toasts.value.find((t) => t.id === id)
    if (toast && !toast.isPaused && toast.duration !== false) {
      toast.isPaused = true
      toast.remainingTime = (toast.duration as number) - (Date.now() - toast.createdAt)
    }
  }

  function resume(id: string): void {
    const toast = toasts.value.find((t) => t.id === id)
    if (toast && toast.isPaused) {
      toast.isPaused = false
      toast.createdAt = Date.now() - ((toast.duration as number) - toast.remainingTime)
    }
  }

  function update(id: string, updates: Partial<Omit<Toast, 'id'>>): void {
    const toast = toasts.value.find((t) => t.id === id)
    if (toast) {
      Object.assign(toast, updates)
    }
  }

  function clear(): void {
    toasts.value = []
  }

  function configure(newConfig: Partial<ToastStoreConfig>): void {
    Object.assign(config.value, newConfig)
  }

  return {
    toasts,
    config,
    visibleToasts,
    hasToasts,
    add,
    remove,
    pause,
    resume,
    update,
    clear,
    configure,
  }
})
